import React, { useState, useEffect } from 'react';
import { 
  Mail, Clock, User, MessageSquare, CheckCircle, XCircle, 
  Eye, Trash2, RefreshCw, LogOut, Shield, AlertCircle,
  Inbox, CheckSquare, MessageCircle, Archive
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [authError, setAuthError] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, reviewed: 0, responded: 0, closed: 0 });
  const [loading, setLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [filter, setFilter] = useState('all');

  const API_URL = (process.env.REACT_APP_BACKEND_URL || '');

  const getAuthHeader = () => {
    const encoded = btoa(`${credentials.username}:${credentials.password}`);
    return { 'Authorization': `Basic ${encoded}` };
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/verify`, {
        method: 'POST',
        headers: getAuthHeader(),
      });

      if (response.ok) {
        setIsAuthenticated(true);
        fetchData();
      } else {
        setAuthError('Ungültige Anmeldedaten');
      }
    } catch (err) {
      setAuthError('Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCredentials({ username: '', password: '' });
    setSubmissions([]);
    setSelectedSubmission(null);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [submissionsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/contacts`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api/admin/stats`, { headers: getAuthHeader() })
      ]);

      if (submissionsRes.ok && statsRes.ok) {
        const submissionsData = await submissionsRes.json();
        const statsData = await statsRes.json();
        setSubmissions(submissionsData);
        setStats(statsData);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/contacts/${id}/status`, {
        method: 'PATCH',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        fetchData();
        if (selectedSubmission?.id === id) {
          setSelectedSubmission({ ...selectedSubmission, status: newStatus });
        }
      }
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm('Möchten Sie diese Anfrage wirklich löschen?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/contacts/${id}`, {
        method: 'DELETE',
        headers: getAuthHeader(),
      });

      if (response.ok) {
        fetchData();
        if (selectedSubmission?.id === id) {
          setSelectedSubmission(null);
        }
      }
    } catch (err) {
      console.error('Error deleting submission:', err);
    }
  };

  const filteredSubmissions = filter === 'all' 
    ? submissions 
    : submissions.filter(s => s.status === filter);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'reviewed': return 'bg-blue-100 text-blue-800';
      case 'responded': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Ausstehend';
      case 'reviewed': return 'Geprüft';
      case 'responded': return 'Beantwortet';
      case 'closed': return 'Geschlossen';
      default: return status;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#003366] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h1 className="text-2xl font-bold text-[#003366]">Admin Dashboard</h1>
              <p className="text-black/60 mt-2">Yetkin Dienstleistungen</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              {authError && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {authError}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="username" className="text-black">Benutzername</Label>
                <Input
                  id="username"
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  required
                  className="h-12"
                  placeholder="admin"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-black">Passwort</Label>
                <Input
                  id="password"
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  required
                  className="h-12"
                  placeholder="••••••••"
                />
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full h-12 bg-[#D4AF37] hover:bg-[#C9A227] text-[#003366] font-semibold"
              >
                {loading ? 'Anmelden...' : 'Anmelden'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      {/* Header */}
      <header className="bg-[#003366] text-white py-4 px-6 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Shield className="w-8 h-8 text-[#D4AF37]" />
            <div>
              <h1 className="text-xl font-bold">Admin Dashboard</h1>
              <p className="text-white/60 text-sm">Yetkin Dienstleistungen</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              onClick={fetchData} 
              variant="outline" 
              size="sm"
              className="border-white/30 text-white hover:bg-white/10"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
            <Button 
              onClick={handleLogout} 
              variant="outline" 
              size="sm"
              className="border-white/30 text-white hover:bg-white/10"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Abmelden
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {[
            { label: 'Gesamt', value: stats.total, icon: Inbox, color: 'bg-[#003366]' },
            { label: 'Ausstehend', value: stats.pending, icon: Clock, color: 'bg-yellow-500' },
            { label: 'Geprüft', value: stats.reviewed, icon: Eye, color: 'bg-blue-500' },
            { label: 'Beantwortet', value: stats.responded, icon: MessageCircle, color: 'bg-green-500' },
            { label: 'Geschlossen', value: stats.closed, icon: Archive, color: 'bg-gray-500' },
          ].map((stat, i) => (
            <div key={i} className="bg-white rounded-xl p-4 shadow-sm border border-black/5">
              <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center mb-3`}>
                <stat.icon className="w-5 h-5 text-white" />
              </div>
              <p className="text-2xl font-bold text-black">{stat.value}</p>
              <p className="text-black/60 text-sm">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {['all', 'pending', 'reviewed', 'responded', 'closed'].map((f) => (
            <Button
              key={f}
              onClick={() => setFilter(f)}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              className={filter === f ? 'bg-[#D4AF37] hover:bg-[#C9A227] text-[#003366]' : ''}
            >
              {f === 'all' ? 'Alle' : getStatusLabel(f)}
            </Button>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Submissions List */}
          <div className="bg-white rounded-2xl shadow-sm border border-black/5 overflow-hidden">
            <div className="p-4 border-b border-black/5">
              <h2 className="font-semibold text-black">Kontaktanfragen ({filteredSubmissions.length})</h2>
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              {filteredSubmissions.length === 0 ? (
                <div className="p-8 text-center text-black/40">
                  <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Keine Anfragen gefunden</p>
                </div>
              ) : (
                filteredSubmissions.map((sub) => (
                  <div
                    key={sub.id}
                    onClick={() => setSelectedSubmission(sub)}
                    className={`p-4 border-b border-black/5 cursor-pointer hover:bg-[#F9F9F9] transition-colors ${
                      selectedSubmission?.id === sub.id ? 'bg-[#D4AF37]/10' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-black truncate">{sub.firstName} {sub.lastName}</p>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(sub.status)}`}>
                            {getStatusLabel(sub.status)}
                          </span>
                        </div>
                        <p className="text-sm text-black/70 truncate">{sub.subject}</p>
                        <p className="text-xs text-black/40 mt-1">{formatDate(sub.created_at)}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); deleteSubmission(sub.id); }}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Detail View */}
          <div className="bg-white rounded-2xl shadow-sm border border-black/5 overflow-hidden">
            <div className="p-4 border-b border-black/5">
              <h2 className="font-semibold text-black">Details</h2>
            </div>
            {selectedSubmission ? (
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(selectedSubmission.status)}`}>
                    {getStatusLabel(selectedSubmission.status)}
                  </span>
                  <p className="text-sm text-black/40">{formatDate(selectedSubmission.created_at)}</p>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-[#D4AF37]" />
                    <div>
                      <p className="text-sm text-black/50">Name</p>
                      <p className="font-medium text-black">{selectedSubmission.firstName} {selectedSubmission.lastName}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-[#D4AF37]" />
                    <div>
                      <p className="text-sm text-black/50">E-Mail</p>
                      <a href={`mailto:${selectedSubmission.email}`} className="font-medium text-[#D4AF37] hover:underline">
                        {selectedSubmission.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MessageSquare className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                    <div>
                      <p className="text-sm text-black/50">Betreff</p>
                      <p className="font-medium text-black">{selectedSubmission.subject}</p>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <p className="text-sm text-black/50 mb-2">Nachricht</p>
                  <div className="bg-[#F9F9F9] rounded-lg p-4 text-black/80 whitespace-pre-wrap">
                    {selectedSubmission.message}
                  </div>
                </div>

                <div>
                  <p className="text-sm text-black/50 mb-3">Status ändern</p>
                  <div className="flex flex-wrap gap-2">
                    {['pending', 'reviewed', 'responded', 'closed'].map((status) => (
                      <Button
                        key={status}
                        onClick={() => updateStatus(selectedSubmission.id, status)}
                        variant={selectedSubmission.status === status ? 'default' : 'outline'}
                        size="sm"
                        className={selectedSubmission.status === status ? 'bg-[#D4AF37] hover:bg-[#C9A227] text-[#003366]' : ''}
                      >
                        {getStatusLabel(status)}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-black/5">
                  <a
                    href={`mailto:${selectedSubmission.email}?subject=Re: ${selectedSubmission.subject}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#003366] text-white rounded-lg hover:bg-[#002244] transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    E-Mail beantworten
                  </a>
                </div>
              </div>
            ) : (
              <div className="p-8 text-center text-black/40">
                <Eye className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Wählen Sie eine Anfrage aus</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
